import { AutoComplete, Button as AntdButton, Input } from 'antd'
import { useKestrel } from 'features/kestrelViewer'
import {
  getTagDatabaseKey,
  getTagDisplayName,
} from 'features/kestrelViewer/hooks/useTagOptions'
import React, { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { KVAnnotation, KVTagMetadata } from 'types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const FormRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`

const FormField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  position: relative;
  z-index: 1;
`

const FieldLabel = styled.label`
  font-size: 0.65rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  font-weight: 500;
`

const StyledAutoComplete = styled(AutoComplete)`
  width: 100%;

  .ant-select-selector {
    cursor: text;
    pointer-events: auto;
  }

  .ant-select-selection-search {
    cursor: text;
    pointer-events: auto;
  }

  .ant-select-selection-search-input {
    color: white;
    cursor: text;
    pointer-events: auto;
  }

  .ant-select-selection-item {
    color: white;
  }

  .ant-select-selection-search-input {
    pointer-events: auto;
  }
`

const StyledButton = styled(AntdButton)`
  height: 32px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const TagsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.5rem;
`

const TagItem = styled.div<{ $isDerived?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0.5rem;
  background-color: ${({ theme, $isDerived }) =>
    $isDerived ? theme.forecast.background2 : theme.forecast.defaultButton};
  border-radius: 4px;
  border: 1px solid
    ${({ theme, $isDerived }) =>
      $isDerived ? theme.forecast.border : theme.forecast.border2};
  font-size: 0.75rem;
`

const TagText = styled.span<{ $isDerived?: boolean }>`
  color: ${({ theme, $isDerived }) =>
    $isDerived ? theme.forecast.secondaryText : theme.forecast.defaultText};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const DerivedBadge = styled.span`
  font-size: 0.6rem;
  padding: 0.1rem 0.3rem;
  background-color: ${({ theme }) => theme.forecast.background4};
  border-radius: 3px;
  color: ${({ theme }) => theme.forecast.textDisabled};
  text-transform: uppercase;
  letter-spacing: 0.05rem;
`

const TagDeleteButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.forecast.text3};
  cursor: pointer;
  padding: 0;
  font-size: 1.25rem;
  margin-left: 0.5rem;
  line-height: 1;
  display: flex;
  align-items: center;

  &:hover {
    color: ${({ theme }) => theme.forecast.danger};
  }
`

const WarningMessage = styled.div`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.forecast.warning};
  margin-top: 0.25rem;
  padding: 0.25rem 0.5rem;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.forecast.warning};
  opacity: 0.9;
`

interface TagSelectorProps {
  editedAnnotation: KVAnnotation
  setEditedAnnotation: React.Dispatch<React.SetStateAction<KVAnnotation>>
}

export const TagSelector: FC<TagSelectorProps> = ({
  editedAnnotation,
  setEditedAnnotation,
}) => {
  const { tagOptions, setTagOptions, annotations, valueOverrides } =
    useKestrel()

  const [tagValue, setTagValue] = useState<string>('')
  const [valueValue, setValueValue] = useState<string>('')

  // Get current tags for the annotation (filter out empty values)
  const currentTags = useMemo(() => {
    const tags = editedAnnotation.tags || {}
    const filteredTags: Record<string, KVTagMetadata> = {}
    for (const [key, metadata] of Object.entries(tags)) {
      if (metadata.value && metadata.value.trim() !== '') {
        filteredTags[key] = metadata
      }
    }
    return filteredTags
  }, [editedAnnotation.tags])

  // Build a set of all derived tag keys from all annotations
  const derivedTagKeys = useMemo(() => {
    const derivedKeys = new Set<string>()
    if (annotations) {
      for (const annotation of annotations) {
        if (annotation.tags) {
          for (const [key, metadata] of Object.entries(annotation.tags)) {
            if (metadata.derivedTag) {
              derivedKeys.add(key)
            }
          }
        }
      }
    }
    return derivedKeys
  }, [annotations])

  // Get all tag names for autocomplete (exclude derived tag keys)
  // Use display names for labels but keep database keys as values
  const tagOptionsList = useMemo(() => {
    return Object.keys(tagOptions)
      .filter((tag) => !derivedTagKeys.has(tag))
      .map((tag) => ({
        value: getTagDisplayName(tag),
        label: getTagDisplayName(tag),
      }))
  }, [tagOptions, derivedTagKeys])

  // Get value options for selected tag (filter out empty values)
  // Convert display name back to database key for lookup, and show display values
  // Apply valueOverrides to display labels if available
  const valueOptionsList = useMemo(() => {
    if (!tagValue) {
      return []
    }
    const databaseKey = getTagDatabaseKey(tagValue.trim())
    if (!tagOptions[databaseKey]) {
      return []
    }
    return Array.from(tagOptions[databaseKey])
      .filter((value) => value && value.trim() !== '')
      .map((value) => {
        // Use override label if available, otherwise use original value
        const displayLabel = valueOverrides[value] || value
        return {
          value: value,
          label: displayLabel,
        }
      })
  }, [tagValue, tagOptions, valueOverrides])

  // Check if the tag being entered is a derived tag (check globally across all annotations)
  // Convert display name back to database key for checking
  const isDerivedTagKey = useMemo(() => {
    const trimmedTag = tagValue.trim()
    if (!trimmedTag) return false
    const databaseKey = getTagDatabaseKey(trimmedTag)
    return derivedTagKeys.has(databaseKey)
  }, [tagValue, derivedTagKeys])

  // Check if submit button should be disabled
  const isSubmitDisabled =
    !tagValue.trim() || !valueValue.trim() || isDerivedTagKey

  // Handle form submission
  const handleSubmit = useCallback(() => {
    if (isSubmitDisabled) return

    const trimmedDisplayTag = tagValue.trim()
    const trimmedDisplayValue = valueValue.trim()

    // Convert display names back to database keys for storage
    const databaseKey = getTagDatabaseKey(trimmedDisplayTag)
    const databaseValue = trimmedDisplayValue

    // Prevent adding tags with keys that are derived anywhere in the system
    if (derivedTagKeys.has(databaseKey)) {
      return
    }

    // Add tag to annotation (user-added tags are not derived)
    // Store using database key and value
    const newTags: Record<string, KVTagMetadata> = {
      ...currentTags,
      [databaseKey]: { value: databaseValue, derivedTag: false },
    }

    setEditedAnnotation((prev) => ({
      ...prev,
      tags: newTags,
    }))

    // Update global tag options using database key and value
    setTagOptions((prev) => {
      const newTagOptions = { ...prev }
      if (!newTagOptions[databaseKey]) {
        newTagOptions[databaseKey] = new Set<string>()
      } else {
        // Create a new Set to ensure React detects the change
        newTagOptions[databaseKey] = new Set(newTagOptions[databaseKey])
      }
      newTagOptions[databaseKey].add(databaseValue)
      return newTagOptions
    })

    // Clear form
    setTagValue('')
    setValueValue('')
  }, [
    isSubmitDisabled,
    tagValue,
    valueValue,
    currentTags,
    derivedTagKeys,
    setEditedAnnotation,
    setTagOptions,
  ])

  // Handle tag deletion (only allow deletion of non-derived tags)
  const handleDeleteTag = useCallback(
    (tagToDelete: string) => {
      // Check if tag is derived - if so, don't allow deletion
      if (currentTags[tagToDelete]?.derivedTag) {
        return
      }

      const newTags = { ...currentTags }
      delete newTags[tagToDelete]

      setEditedAnnotation((prev) => ({
        ...prev,
        tags: newTags,
      }))
    },
    [currentTags, setEditedAnnotation]
  )

  return (
    <Container>
      <FormRow>
        <FormField>
          <FieldLabel htmlFor='tag-selector-tag'>Tag</FieldLabel>
          <StyledAutoComplete
            value={tagValue}
            options={tagOptionsList}
            onSearch={(searchTerm) => {
              setTagValue(searchTerm || '')
              // Clear value when tag changes
              setValueValue('')
            }}
            onChange={(value) => {
              const stringValue = String(value || '')
              setTagValue(stringValue)
              // Clear value when tag changes
              setValueValue('')
            }}
            onSelect={(value) => {
              setTagValue(String(value))
              setValueValue('')
            }}
            allowClear
            defaultActiveFirstOption={false}
            open={undefined}
            filterOption={(inputValue, option) => {
              if (!option || !inputValue) return true
              return (
                option.value
                  ?.toLowerCase()
                  .includes(inputValue.toLowerCase()) ?? false
              )
            }}
            onKeyDown={(e) => {
              e.stopPropagation()

              if (e.key === 'Enter' && !isSubmitDisabled) {
                e.preventDefault()
                handleSubmit()
              }
            }}
            dropdownStyle={{
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
            }}
            popupClassName='tag-autocomplete-dropdown'
            notFoundContent={null}>
            <Input
              id='tag-selector-tag'
              placeholder='Select or type tag name'
            />
          </StyledAutoComplete>
        </FormField>
        <FormField>
          <FieldLabel htmlFor='tag-selector-value'>Value</FieldLabel>
          <StyledAutoComplete
            value={valueValue}
            options={valueOptionsList}
            onSearch={(searchTerm) => {
              setValueValue(searchTerm || '')
            }}
            onChange={(value) => {
              const stringValue = String(value || '')
              setValueValue(stringValue)
            }}
            onSelect={(value) => setValueValue(String(value))}
            disabled={!tagValue}
            allowClear
            defaultActiveFirstOption={false}
            open={undefined}
            filterOption={(inputValue, option) => {
              if (!option || !inputValue) return true
              return (
                option.value
                  ?.toLowerCase()
                  .includes(inputValue.toLowerCase()) ?? false
              )
            }}
            onKeyDown={(e) => {
              e.stopPropagation()

              if (e.key === 'Enter' && !isSubmitDisabled) {
                e.preventDefault()
                handleSubmit()
              }
            }}
            dropdownStyle={{
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
            }}
            popupClassName='tag-autocomplete-dropdown'
            notFoundContent={null}>
            <Input
              id='tag-selector-value'
              placeholder={
                tagValue ? 'Select or type value' : 'Select a tag first'
              }
              disabled={!tagValue}
            />
          </StyledAutoComplete>
        </FormField>
        <StyledButton
          type='default'
          onClick={handleSubmit}
          disabled={isSubmitDisabled}>
          Add
        </StyledButton>
      </FormRow>

      {/* Warning message when trying to add a derived tag */}
      {isDerivedTagKey && (
        <WarningMessage>
          Cannot add or modify a derived tag. This tag is automatically managed.
        </WarningMessage>
      )}

      {/* Display current tags */}
      {Object.keys(currentTags).length > 0 && (
        <TagsList>
          {Object.entries(currentTags)
            .sort(([, metadataA], [, metadataB]) => {
              // Sort so that user-defined tags (derivedTag === false/undefined) come first
              // and derived tags (derivedTag === true) come last
              if (metadataA.derivedTag && !metadataB.derivedTag) return 1
              if (!metadataA.derivedTag && metadataB.derivedTag) return -1
              return 0
            })
            .map(([tag, metadata]) => {
              // Apply valueOverrides to display value if available
              const displayValue =
                valueOverrides[metadata.value] || metadata.value
              return (
                <TagItem key={tag} $isDerived={metadata.derivedTag}>
                  <TagText $isDerived={metadata.derivedTag}>
                    <span>
                      {getTagDisplayName(tag)}: {displayValue}
                    </span>
                    {metadata.derivedTag && (
                      <DerivedBadge>derived</DerivedBadge>
                    )}
                  </TagText>
                  {!metadata.derivedTag && (
                    <TagDeleteButton
                      onClick={() => handleDeleteTag(tag)}
                      title='Delete tag'>
                      ×
                    </TagDeleteButton>
                  )}
                </TagItem>
              )
            })}
        </TagsList>
      )}
    </Container>
  )
}
